/**
 * Sprint 25.3 — pure helpers for the "Recently" timeline.
 *
 * Extracted from RecentlyTimeline so the transition→prose vocabulary and the
 * friendly relative-time formatting can be unit-tested without React
 * (scripts/test-brain-snapshot-25.3.ts).
 */

import type { RecentEvent } from "../../lib/types";

export const INITIAL_FROM_STATE = "(created)";

export interface EventSegment {
  text: string;
  strong?: boolean;
}

/** Friendly relative time: "12m ago" / "1h ago" / "yesterday" / "2d ago". */
export function relativeTime(iso: string, nowMs: number = Date.now()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  return `${day}d ago`;
}

/**
 * Turn a transition into a prose sentence (segments; strong=true marks the
 * primary nouns). Deterministic — derived from from/to state + terminality.
 */
export function describeRecentEvent(e: RecentEvent): EventSegment[] {
  const title = e.task_title ?? e.workflow_name ?? "a task";

  // Started — the initial transition out of "(created)".
  if (e.from_state === INITIAL_FROM_STATE) {
    return [{ text: "Picked up " }, { text: title, strong: true }];
  }

  // Completed — landed in a terminal state.
  if (e.to_is_terminal) {
    return [{ text: "Completed " }, { text: title, strong: true }];
  }

  // Surfaced for a human — a user advanced the workflow.
  if (e.actor_kind === "user") {
    return [
      { text: "Surfaced " },
      { text: title, strong: true },
      { text: " as a decision for you" },
    ];
  }

  // Generic transition — "Transitioned X from Y → Z".
  const from = e.from_label ?? e.from_state;
  const to = e.to_label ?? e.to_state;
  return [
    { text: "Transitioned " },
    { text: title, strong: true },
    { text: ` from ${from} → ` },
    { text: to, strong: true },
  ];
}
